import { useState, useMemo, useEffect } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import ActionMenu, { type ActionMenuItem } from "../components/ActionMenu";
import DateRangePicker from "../components/DateRangePicker";
import InlineStatusBadge from "../components/InlineStatusBadge";
import { eventsApi, apiErrorMessage } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { useSettings } from "@/context/SettingsContext";
import { formatCurrencyFull, formatDate, localToday } from "@/lib/data";
import ClientPaymentModal from "./components/ClientPaymentModal";
import VendorPaymentModal from "./components/VendorPaymentModal";
import PrintableInvoice from "./components/PrintableInvoice";
import AddEventModal from "./components/AddEventModal";
import EditEventModal from "./components/EditEventModal";
import {
  ChevronUp, ChevronDown, ChevronsUpDown, Sheet, Eye, Pencil, Printer, HandCoins, Trash2, Plus, Search,
  CalendarX, Ban, SlidersHorizontal, X, Users, RefreshCw, AlertTriangle,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Event {
  id: number;
  name: string;
  client: { id: number; name: string; phone?: string | null } | null;
  package: { id: number; name: string } | null;
  venue?: { id: number; name: string; location: string } | null;
  start_date: string;
  end_date: string | null;
  guest_count: number;
  status: string;
  payment_status?: string;
  event_type?: string;
  notes?: string;
  // Decimal columns may arrive as strings; num() handles both
  total_amount?: number;
  created_at?: string;
  client_payments?: { amount: number | string }[];
  services?: unknown[];
  event_vendors?: unknown[];
  services_data?: any[];
  vendors_data?: any[];
  financials?: {
    income_subtotal: number;
    net_revenue?: number;
    final_revenue?: number;
    total_cost: number;
    total_expense?: number;
    profit: number;
    profit_margin?: number;
    customer: { total: number; paid: number; balance: number };
    vendor_totals?: { paid: number; balance: number; cost: number };
    vendors?: any[];
  };
}

type View = "upcoming" | "month" | "past" | "all";
type PayState = "paid" | "partial" | "unpaid" | "overdue" | "none";
type PayFilter = "" | "due" | "overdue" | "paid" | "unpaid";
type SortCol = "name" | "date" | "guests" | "revenue" | "balance" | "profit";

interface Row {
  e: Event;
  start: string;
  end: string;
  days: number;
  /** days from today to the start (negative = started in the past) */
  offset: number;
  ongoing: boolean;
  ended: boolean;
  revenue: number;
  cost: number;
  profit: number;
  margin: number;
  paid: number;
  balance: number;
  pay: PayState;
  canTakePayment: boolean;
  vendorDue: number;
}

// ─── Constants & helpers ─────────────────────────────────────────────────────

const STATUS_OPTIONS = ["draft", "confirmed", "in_progress", "completed", "cancelled"];
/** Mirrors Event::STATUS_TRANSITIONS; replaced by the server's copy once the list loads */
const DEFAULT_TRANSITIONS: Record<string, string[]> = {
  draft: ["confirmed", "cancelled"],
  confirmed: ["in_progress", "draft", "cancelled"],
  in_progress: ["completed", "cancelled"],
  completed: ["in_progress"],
  cancelled: ["draft", "confirmed"],
};
const BILLABLE = ["confirmed", "in_progress", "completed"];
const statusLabel = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace("_", " ");
const d10 = (s?: string | null) => (s || "").slice(0, 10);
/** Whole days between two YYYY-MM-DD dates */
const dayDiff = (a: string, b: string) => Math.round((Date.parse(a) - Date.parse(b)) / 86_400_000);
const num = (v: unknown) => Number(v) || 0;

const VIEWS: { value: View; label: string }[] = [
  { value: "upcoming", label: "Upcoming" },
  { value: "month", label: "This month" },
  { value: "past", label: "Past" },
  { value: "all", label: "All" },
];

const PAY_LABEL: Record<PayState, string> = { paid: "Paid", partial: "Partial", unpaid: "Unpaid", overdue: "Overdue", none: "" };
const PAY_CLASS: Record<PayState, string> = {
  paid: "bg-emerald-50 text-emerald-700",
  partial: "bg-amber-100 text-amber-800",
  unpaid: "bg-stone-100 text-stone-600",
  overdue: "bg-red-50 text-red-700",
  none: "",
};

function toRow(e: Event, today: string): Row {
  const start = d10(e.start_date) || today;
  const end = d10(e.end_date) || start;
  const offset = dayDiff(start, today);
  const ended = dayDiff(end, today) < 0;
  const f = e.financials;
  // Revenue after discount (before tax) − cost incl. event expenses = profit
  const revenue = num(f?.net_revenue ?? f?.income_subtotal ?? e.total_amount);
  const cost = num(f?.total_expense ?? f?.total_cost);
  const profit = f ? num(f.profit) : revenue - cost;
  const paid = num(f?.customer?.paid);
  const balance = num(f?.customer?.balance);
  let pay: PayState = "none";
  if (e.status !== "cancelled") {
    if (balance <= 0 && (paid > 0 || revenue > 0)) pay = "paid";
    else if (balance > 0 && ended && e.status !== "draft") pay = "overdue";
    else if (balance > 0 && paid > 0) pay = "partial";
    else if (balance > 0) pay = "unpaid";
  }
  const vendorDue = num(f?.vendor_totals?.balance);
  return {
    e, start, end,
    days: Math.max(1, dayDiff(end, start) + 1),
    offset, ongoing: offset <= 0 && !ended, ended,
    revenue, cost, profit,
    margin: revenue > 0 ? (profit / revenue) * 100 : 0,
    paid, balance, pay,
    canTakePayment: BILLABLE.includes(e.status) && balance > 0,
    vendorDue: (f?.vendors?.length || 0) > 0 ? vendorDue : 0,
  };
}

function relativeLabel(r: Row): { text: string; tone: string } | null {
  if (r.e.status === "cancelled") return null;
  if (r.offset === 0 || (r.ongoing && r.offset < 0)) return { text: r.offset === 0 ? "Today" : "Ongoing", tone: "bg-amber-600 text-white" };
  if (r.offset === 1) return { text: "Tomorrow", tone: "bg-amber-100 text-amber-800" };
  if (r.offset > 1 && r.offset <= 7) return { text: `in ${r.offset} days`, tone: "bg-amber-100 text-amber-800" };
  if (r.offset > 7) return { text: `in ${r.offset} days`, tone: "bg-stone-100 text-stone-600" };
  const ago = -dayDiff(r.end, localToday());
  return ago <= 60 ? { text: `${ago} day${ago === 1 ? "" : "s"} ago`, tone: "bg-stone-100 text-stone-500" } : null;
}

function dateText(r: Row) {
  if (r.days <= 1) return formatDate(r.start);
  const sameMonth = r.start.slice(0, 7) === r.end.slice(0, 7);
  return sameMonth
    ? `${Number(r.start.slice(8, 10))}–${formatDate(r.end)}`
    : `${formatDate(r.start)} – ${formatDate(r.end)}`;
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function EventsPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { settings } = useSettings();
  const logoUrl = settings?.logoUrl || null;
  const businessName = settings?.business?.businessName || "Shalimar";
  const isStaff = user?.role === "staff";
  const canDelete = user?.role === "admin" || user?.role === "master" || user?.role === "super_admin";
  const canViewProfit = canDelete;

  const [events, setEvents] = useState<Event[]>([]);
  const [transitions, setTransitions] = useState<Record<string, string[]>>(DEFAULT_TRANSITIONS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [view, setView] = useState<View>("all");
  const [statusFilter, setStatusFilter] = useState("");
  const [payFilter, setPayFilter] = useState<PayFilter>("");
  const [venueFilter, setVenueFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState<SortCol | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // Modals
  const [clientPaymentEvent, setClientPaymentEvent] = useState<Event | null>(null);
  const [clientPaymentData, setClientPaymentData] = useState<{ customer: any; payments: any[] } | null>(null);
  const [vendorPaymentEvent, setVendorPaymentEvent] = useState<Event | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [printEvent, setPrintEvent] = useState<Event | null>(null);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [deleting, setDeleting] = useState<Row | null>(null);
  const [busy, setBusy] = useState(false);

  const today = localToday();

  const loadEvents = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await eventsApi.list();
      setEvents(response.data?.data || response.data || []);
      if (response.status_transitions) setTransitions(response.status_transitions);
      setLoadError(null);
    } catch (error) {
      setLoadError(apiErrorMessage(error, "Couldn't load events"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleAfterPrint = () => {
      if (printEvent) showToast("Invoice ready - use browser's save as PDF option", "success");
    };
    window.addEventListener("afterprint", handleAfterPrint);
    return () => window.removeEventListener("afterprint", handleAfterPrint);
  }, [printEvent, showToast]);

  const rows = useMemo(() => events.map((e) => toRow(e, today)), [events, today]);

  // Summary strip (always over all events, independent of the filters)
  const summary = useMemo(() => {
    const live = rows.filter((r) => r.e.status !== "cancelled");
    const next30 = live.filter((r) => (r.offset >= 0 && r.offset <= 30) || r.ongoing);
    const due = live.filter((r) => BILLABLE.includes(r.e.status) && r.balance > 0);
    const overdue = live.filter((r) => r.pay === "overdue");
    const month = live.filter((r) => r.start.slice(0, 7) === today.slice(0, 7));
    const monthRevenue = month.reduce((s, r) => s + r.revenue, 0);
    const monthProfit = month.reduce((s, r) => s + r.profit, 0);
    return {
      next30: next30.length,
      next30Guests: next30.reduce((s, r) => s + num(r.e.guest_count), 0),
      dueCount: due.length,
      dueTotal: due.reduce((s, r) => s + r.balance, 0),
      overdueCount: overdue.length,
      overdueTotal: overdue.reduce((s, r) => s + r.balance, 0),
      monthCount: month.length,
      monthProfit,
      monthMargin: monthRevenue > 0 ? (monthProfit / monthRevenue) * 100 : 0,
    };
  }, [rows, today]);

  const inView = (r: Row, v: View) => {
    if (v === "upcoming") return !r.ended && r.e.status !== "cancelled";
    if (v === "past") return r.ended;
    if (v === "month") return r.start.slice(0, 7) === today.slice(0, 7);
    return true;
  };
  const viewCounts = useMemo(
    () => Object.fromEntries(VIEWS.map((v) => [v.value, rows.filter((r) => inView(r, v.value)).length])) as Record<View, number>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, today]
  );

  const venues = useMemo(() => [...new Set(events.map((e) => e.venue?.name).filter(Boolean) as string[])].sort(), [events]);
  const types = useMemo(() => [...new Set(events.map((e) => e.event_type).filter(Boolean) as string[])].sort(), [events]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter((r) => {
      const e = r.e;
      if (!inView(r, view)) return false;
      if (statusFilter && e.status !== statusFilter) return false;
      if (payFilter === "due" && !(BILLABLE.includes(e.status) && r.balance > 0)) return false;
      if (payFilter === "overdue" && r.pay !== "overdue") return false;
      if (payFilter === "paid" && r.pay !== "paid") return false;
      if (payFilter === "unpaid" && r.pay !== "unpaid") return false;
      if (venueFilter && e.venue?.name !== venueFilter) return false;
      if (typeFilter && e.event_type !== typeFilter) return false;
      if (fromDate && r.end < fromDate) return false;
      if (toDate && r.start > toDate) return false;
      if (q) {
        const hay = [e.name, e.client?.name, e.client?.phone, e.venue?.name, e.event_type, e.package?.name].map((v) => (v || "").toLowerCase());
        if (!hay.some((v) => v.includes(q))) return false;
      }
      return true;
    });

    if (sortCol) {
      const val = (r: Row): number | string =>
        sortCol === "name" ? (r.e.name || "").toLowerCase()
        : sortCol === "date" ? r.start
        : sortCol === "guests" ? num(r.e.guest_count)
        : sortCol === "revenue" ? r.revenue
        : sortCol === "balance" ? r.balance
        : r.profit;
      return [...list].sort((a, b) => {
        const x = val(a), y = val(b);
        const cmp = typeof x === "string" ? x.localeCompare(y as string) : x - (y as number);
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    // Default order per view: upcoming soonest first, past latest first, everything else newest-created first
    if (view === "upcoming" || view === "month") return [...list].sort((a, b) => a.start.localeCompare(b.start));
    if (view === "past") return [...list].sort((a, b) => b.end.localeCompare(a.end));
    return list; // the API already returns newest-created first
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, view, statusFilter, payFilter, venueFilter, typeFilter, fromDate, toDate, search, sortCol, sortDir, today]);

  const extraFilterCount = [payFilter, venueFilter, typeFilter, fromDate || toDate].filter(Boolean).length;
  const anyFilter = !!(search || statusFilter || extraFilterCount || view !== "all");
  const clearFilters = () => {
    setSearch(""); setStatusFilter(""); setPayFilter(""); setVenueFilter(""); setTypeFilter("");
    setFromDate(""); setToDate(""); setView("all"); setSortCol(null);
  };

  const handleSort = (col: SortCol) => {
    if (sortCol !== col) { setSortCol(col); setSortDir(col === "name" || col === "date" ? "asc" : "desc"); }
    else if (sortDir === (col === "name" || col === "date" ? "asc" : "desc")) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else setSortCol(null);
  };

  // ─── Actions ─────────────────────────────────────────────────────────────

  const openEvent = (e: Event) => navigate(`/events/detail?id=${e.id}`);

  const handleStatusChange = async (eventId: number, newStatus: string) => {
    try {
      // The status endpoint enforces allowed transitions and records the change in the event log
      await eventsApi.updateStatus(eventId, newStatus);
      setEvents((prev) => prev.map((e) => (e.id === eventId ? { ...e, status: newStatus } : e)));
      showToast(`Status changed to ${statusLabel(newStatus).toLowerCase()}`, "success");
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't change the status"), "error");
    }
  };

  const openClientPaymentModal = async (event: Event) => {
    setModalLoading(true);
    try {
      const response = await eventsApi.get(event.id);
      const detail = response.data?.data || response.data;
      setClientPaymentData({
        customer: detail.financials?.customer || null,
        payments: detail.client_payments || detail.clientPayments || [],
      });
      setClientPaymentEvent(event);
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't load the event's payments"), "error");
    } finally {
      setModalLoading(false);
    }
  };

  const printEventInvoice = (event: Event) => {
    setPrintEvent(event);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintEvent(null), 100);
    }, 100);
  };

  const canCancel = (e: Event) => !isStaff && (transitions[e.status] || []).includes("cancelled");

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await eventsApi.delete(deleting.e.id);
      showToast(`“${deleting.e.name}” deleted`, "success");
      setDeleting(null);
      loadEvents(true);
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't delete the event"), "error");
    } finally {
      setBusy(false);
    }
  };

  const cancelInstead = async () => {
    if (!deleting) return;
    setBusy(true);
    await handleStatusChange(deleting.e.id, "cancelled");
    setBusy(false);
    setDeleting(null);
  };

  const menuItems = (r: Row): ActionMenuItem[] => {
    const e = r.e;
    const items: ActionMenuItem[] = [
      { label: "Open event", icon: Eye, onClick: () => openEvent(e) },
      { label: "Edit", icon: Pencil, onClick: () => setEditingEvent(e) },
      { label: "Print invoice", icon: Printer, onClick: () => printEventInvoice(e) },
    ];
    if (r.vendorDue > 0) items.push({ label: "Pay vendor", icon: HandCoins, hint: formatCurrencyFull(r.vendorDue), onClick: () => setVendorPaymentEvent(e) });
    if (canCancel(e) && e.status !== "cancelled") items.push({ label: "Cancel event", icon: Ban, separated: true, onClick: () => handleStatusChange(e.id, "cancelled") });
    if (canDelete) items.push({ label: "Delete…", icon: Trash2, danger: true, separated: !canCancel(e), onClick: () => setDeleting(r) });
    return items;
  };

  const exportToExcel = () => {
    const header = ["#", "Event", "Type", "Client", "Phone", "Venue", "Start", "End", "Days", "Guests", "Status",
      ...(canViewProfit ? ["Revenue (₹)", "Cost (₹)", "Profit (₹)"] : []), "Paid (₹)", "Balance (₹)", "Payment"];
    const body = filtered.map((r, i) => [
      i + 1, r.e.name || "", r.e.event_type || "", r.e.client?.name || "", r.e.client?.phone || "", r.e.venue?.name || "",
      r.start, r.end, r.days, num(r.e.guest_count), statusLabel(r.e.status),
      ...(canViewProfit ? [r.revenue, r.cost, r.profit] : []), r.paid, r.balance, PAY_LABEL[r.pay],
    ]);
    const sum = (k: "revenue" | "cost" | "profit" | "paid" | "balance") => filtered.reduce((s, r) => s + r[k], 0);
    const total = ["TOTAL", "", "", "", "", "", "", "", "", filtered.reduce((s, r) => s + num(r.e.guest_count), 0), "",
      ...(canViewProfit ? [sum("revenue"), sum("cost"), sum("profit")] : []), sum("paid"), sum("balance"), ""];
    const ws = XLSX.utils.aoa_to_sheet([header, ...body, total]);
    ws["!cols"] = header.map((h) => ({ wch: Math.max(8, h.length + 2) }));
    ws["!cols"][1] = { wch: 28 };
    ws["!views"] = [{ state: "frozen", xSplit: 0, ySplit: 1, topLeftCell: "A2", activeCell: "A2", sqref: "A2" }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Events");
    XLSX.writeFile(wb, `events_${today}.xlsx`);
    showToast(`Exported ${filtered.length} events`, "success");
  };

  // ─── Rendering helpers ───────────────────────────────────────────────────

  const SortTh = ({ col, label, align = "left" }: { col: SortCol; label: string; align?: "left" | "right" }) => {
    const Icon = sortCol !== col ? ChevronsUpDown : sortDir === "asc" ? ChevronUp : ChevronDown;
    return (
      <th
        onClick={() => handleSort(col)}
        aria-sort={sortCol === col ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
        className={`px-3 py-3 text-${align} text-[11px] font-bold uppercase tracking-wide text-stone-500 cursor-pointer select-none hover:bg-stone-100 whitespace-nowrap`}
      >
        {label}<Icon className={`inline w-3 h-3 ml-0.5 ${sortCol === col ? "text-amber-600" : "opacity-30"}`} />
      </th>
    );
  };

  const payPill = (r: Row) =>
    r.pay === "none" ? null : (
      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${PAY_CLASS[r.pay]}`}>{PAY_LABEL[r.pay]}</span>
    );

  const relPill = (r: Row) => {
    const rel = relativeLabel(r);
    return rel ? <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${rel.tone}`}>{rel.text}</span> : null;
  };

  const statusCell = (e: Event) => (
    <InlineStatusBadge
      status={e.status}
      options={[e.status, ...(transitions[e.status] || [])]}
      onChange={(s) => handleStatusChange(e.id, s)}
      disabled={isStaff}
    />
  );

  const takeButton = (r: Row, big = false) =>
    r.canTakePayment ? (
      <button
        type="button"
        onClick={(ev) => { ev.stopPropagation(); openClientPaymentModal(r.e); }}
        className={`${big ? "h-10 px-3.5 text-sm" : "h-8 px-2.5 text-xs"} inline-flex items-center rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold whitespace-nowrap tabular-nums`}
        title="Record a client payment"
      >
        Take {formatCurrencyFull(r.balance)}
      </button>
    ) : null;

  const money = (n: number) => formatCurrencyFull(n);
  const profitClass = (n: number) => (n < 0 ? "text-red-600" : "text-emerald-700");

  // Upcoming view on phones: grouped by when
  const groups = useMemo(() => {
    if (view !== "upcoming" || sortCol) return null;
    const label = (r: Row) =>
      r.offset <= 0 ? "Today" : r.offset <= 7 ? "This week" : r.start.slice(0, 7) === today.slice(0, 7) ? "Later this month" : "Later";
    const out: { label: string; rows: Row[] }[] = [];
    for (const r of filtered) {
      const l = label(r);
      const g = out.find((x) => x.label === l);
      if (g) g.rows.push(r); else out.push({ label: l, rows: [r] });
    }
    return out;
  }, [filtered, view, sortCol, today]);

  const card = (r: Row) => {
    const e = r.e;
    const isToday = r.offset === 0 || (r.ongoing && e.status !== "cancelled");
    return (
      <div key={e.id} className={`p-4 ${isToday ? "bg-amber-50/40" : ""} ${r.ended && e.status !== "cancelled" ? "opacity-80" : ""}`}>
        <div className="flex items-start justify-between gap-3">
          <button type="button" className="min-w-0 text-left" onClick={() => openEvent(e)}>
            <p className="font-semibold text-stone-800 break-words">{e.name}</p>
            <p className="text-xs text-stone-500 mt-0.5 truncate">
              {[e.client?.name, e.venue?.name, e.guest_count ? `${e.guest_count} guests` : ""].filter(Boolean).join(" · ") || "No client"}
            </p>
          </button>
          {statusCell(e)}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
          <div className="min-w-0">
            <p className="text-[11px] text-stone-400">Date</p>
            <p className="text-stone-800 font-medium truncate">{dateText(r)}</p>
            <div className="mt-0.5">{relPill(r)}</div>
          </div>
          <div>
            <p className="text-[11px] text-stone-400">{canViewProfit ? "Revenue" : "Guests"}</p>
            <p className="text-stone-800 font-medium tabular-nums">{canViewProfit ? money(r.revenue) : num(e.guest_count)}</p>
            {canViewProfit && <p className={`text-[11px] font-semibold tabular-nums ${profitClass(r.profit)}`}>{r.profit < 0 ? "Loss " : "Profit "}{money(Math.abs(r.profit))}</p>}
          </div>
          <div>
            <p className="text-[11px] text-stone-400">Balance due</p>
            <p className={`font-semibold tabular-nums ${r.balance > 0 ? "text-amber-700" : "text-stone-500"}`}>{r.balance > 0 ? money(r.balance) : "—"}</p>
            <div className="mt-0.5">{payPill(r)}</div>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          {takeButton(r, true)}
          <ActionMenu items={menuItems(r)} label={`More actions for ${e.name}`} />
        </div>
      </div>
    );
  };

  // ─── Loading / error ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <DashboardLayout>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-[76px] rounded-xl bg-stone-100 animate-pulse" />)}
        </div>
        <div className="h-10 w-72 max-w-full rounded-xl bg-stone-100 animate-pulse mb-3" />
        <div className="bg-white border border-stone-200 rounded-xl divide-y divide-stone-100" aria-label="Loading events">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="p-4 flex gap-4 items-center">
              <div className="flex-1 space-y-2"><div className="h-4 w-1/3 rounded bg-stone-100 animate-pulse" /><div className="h-3 w-1/4 rounded bg-stone-100 animate-pulse" /></div>
              <div className="h-4 w-20 rounded bg-stone-100 animate-pulse" />
            </div>
          ))}
        </div>
      </DashboardLayout>
    );
  }

  if (loadError) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-stone-200 rounded-xl p-10 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="font-semibold text-stone-800">Couldn't load events</p>
          <p className="text-sm text-stone-500 mt-1">{loadError}</p>
          <button type="button" onClick={() => loadEvents()} className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50">
            <RefreshCw className="w-4 h-4" /> Try again
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const kpi = "text-left rounded-xl border px-3.5 py-3 transition-colors hover:border-amber-300";
  const chip = (active: boolean) =>
    `shrink-0 min-h-9 px-3 rounded-full border text-xs font-semibold whitespace-nowrap transition-colors ${
      active ? "border-amber-500 bg-amber-50 text-amber-800" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"
    }`;
  const selectCls = "w-full h-10 px-3 rounded-xl border border-stone-200 text-sm bg-white text-stone-700 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100";

  const emptyState = (
    <div className="text-center py-12 px-4">
      <CalendarX className="w-12 h-12 mx-auto mb-3 text-stone-300" />
      {events.length === 0 ? (
        <>
          <p className="text-stone-600 font-medium">No events yet</p>
          <button type="button" onClick={() => setShowAddEventModal(true)} className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90">
            <Plus className="w-4 h-4" /> New event
          </button>
        </>
      ) : (
        <>
          <p className="text-stone-600 font-medium">No events match these filters</p>
          <button type="button" onClick={clearFilters} className="mt-3 text-sm font-semibold text-amber-700 hover:underline">Clear filters</button>
        </>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search event, client, phone, venue…"
            aria-label="Search events"
            className="w-full h-10 pl-9 pr-3 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={exportToExcel} disabled={filtered.length === 0}
            className="flex-1 sm:flex-none h-10 inline-flex items-center justify-center gap-1.5 px-3 rounded-xl text-sm font-semibold bg-white border border-stone-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">
            <Sheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={() => setShowAddEventModal(true)}
            className="flex-1 sm:flex-none h-10 inline-flex items-center justify-center gap-2 px-4 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 whitespace-nowrap">
            <Plus className="w-4 h-4" /> New event
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className={`grid grid-cols-2 ${canViewProfit ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-3 mb-4`}>
        <button type="button" onClick={() => { clearFilters(); setView("upcoming"); }} className={`${kpi} bg-white border-stone-200`}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-stone-400">Next 30 days</p>
          <p className="text-xl font-bold text-stone-800 tabular-nums">{summary.next30} event{summary.next30 === 1 ? "" : "s"}</p>
          <p className="text-xs text-stone-500 inline-flex items-center gap-1"><Users className="w-3 h-3" />{summary.next30Guests.toLocaleString("en-IN")} guests</p>
        </button>
        <button type="button" onClick={() => { clearFilters(); setPayFilter("due"); setShowFilters(true); }} className={`${kpi} bg-white border-stone-200`}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-stone-400">To collect</p>
          <p className="text-xl font-bold text-stone-800 tabular-nums">{money(summary.dueTotal)}</p>
          <p className="text-xs text-stone-500">from {summary.dueCount} event{summary.dueCount === 1 ? "" : "s"}</p>
        </button>
        <button type="button" onClick={() => { clearFilters(); setPayFilter("overdue"); setShowFilters(true); }}
          className={`${kpi} ${summary.overdueCount ? "bg-red-50 border-red-200" : "bg-white border-stone-200"}`}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-stone-400">Overdue</p>
          <p className={`text-xl font-bold tabular-nums ${summary.overdueCount ? "text-red-700" : "text-stone-800"}`}>{money(summary.overdueTotal)}</p>
          <p className="text-xs text-stone-500">{summary.overdueCount ? `${summary.overdueCount} event${summary.overdueCount === 1 ? "" : "s"}, date passed` : "Nothing overdue"}</p>
        </button>
        {canViewProfit && (
          <button type="button" onClick={() => { clearFilters(); setView("month"); }} className={`${kpi} bg-white border-stone-200`}>
            <p className="text-[11px] font-bold uppercase tracking-wide text-stone-400">Profit this month</p>
            <p className={`text-xl font-bold tabular-nums ${profitClass(summary.monthProfit)}`}>{money(summary.monthProfit)}</p>
            <p className="text-xs text-stone-500">{summary.monthCount} event{summary.monthCount === 1 ? "" : "s"}{summary.monthCount ? ` · ${summary.monthMargin.toFixed(0)}% margin` : ""}</p>
          </button>
        )}
      </div>

      {/* Views + status chips + more filters */}
      <div className="flex flex-col gap-2 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 p-1 rounded-xl bg-stone-100 border border-stone-200 overflow-x-auto max-w-full" role="group" aria-label="View">
            {VIEWS.map((v) => (
              <button key={v.value} type="button" aria-pressed={view === v.value} onClick={() => { setView(v.value); setSortCol(null); }}
                className={`shrink-0 h-9 px-3.5 rounded-lg text-sm whitespace-nowrap transition-colors ${view === v.value ? "bg-white text-stone-900 font-semibold shadow-sm" : "text-stone-600 hover:text-stone-900"}`}>
                {v.label} <span className="text-xs text-stone-400">{viewCounts[v.value]}</span>
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setShowFilters((s) => !s)} aria-expanded={showFilters}
            className={`h-10 inline-flex items-center gap-1.5 px-3 rounded-xl border text-sm font-semibold ${showFilters || extraFilterCount ? "border-amber-400 bg-amber-50 text-amber-800" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"}`}>
            <SlidersHorizontal className="w-4 h-4" /> Filters{extraFilterCount ? ` (${extraFilterCount})` : ""}
          </button>
          {anyFilter && (
            <button type="button" onClick={clearFilters} className="h-10 inline-flex items-center gap-1 px-3 rounded-xl text-sm text-stone-600 hover:bg-stone-100">
              <X className="w-4 h-4" /> Clear
            </button>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0 pb-1" role="group" aria-label="Status">
          <button type="button" aria-pressed={!statusFilter} onClick={() => setStatusFilter("")} className={chip(!statusFilter)}>Any status</button>
          {STATUS_OPTIONS.map((s) => (
            <button key={s} type="button" aria-pressed={statusFilter === s} onClick={() => setStatusFilter(statusFilter === s ? "" : s)} className={chip(statusFilter === s)}>
              {statusLabel(s)} <span className="text-stone-400 font-normal">{rows.filter((r) => r.e.status === s && inView(r, view)).length}</span>
            </button>
          ))}
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 bg-white border border-stone-200 rounded-xl p-3">
            <div>
              <label htmlFor="ev-pay" className="block text-xs font-medium text-stone-500 mb-1.5">Payment</label>
              <select id="ev-pay" value={payFilter} onChange={(e) => setPayFilter(e.target.value as PayFilter)} className={selectCls}>
                <option value="">Any</option>
                <option value="due">Balance due</option>
                <option value="overdue">Overdue</option>
                <option value="unpaid">Unpaid</option>
                <option value="paid">Fully paid</option>
              </select>
            </div>
            <div>
              <label htmlFor="ev-venue" className="block text-xs font-medium text-stone-500 mb-1.5">Venue</label>
              <select id="ev-venue" value={venueFilter} onChange={(e) => setVenueFilter(e.target.value)} className={selectCls}>
                <option value="">Any venue</option>
                {venues.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="ev-type" className="block text-xs font-medium text-stone-500 mb-1.5">Event type</label>
              <select id="ev-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={selectCls}>
                <option value="">Any type</option>
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <DateRangePicker fromDate={fromDate} toDate={toDate} onFromChange={setFromDate} onToChange={setToDate} />
          </div>
        )}
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        {/* Phone cards */}
        <div className="md:hidden">
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-stone-100 bg-stone-50 text-xs text-stone-500">
            <span>{filtered.length} event{filtered.length === 1 ? "" : "s"}</span>
            <select
              value={sortCol ? `${sortCol}:${sortDir}` : ""}
              onChange={(e) => {
                if (!e.target.value) { setSortCol(null); return; }
                const [c, d] = e.target.value.split(":") as [SortCol, "asc" | "desc"];
                setSortCol(c); setSortDir(d);
              }}
              aria-label="Sort events"
              className="h-10 max-w-[60%] px-3 rounded-lg border border-stone-200 text-sm bg-white text-stone-700 outline-none focus:border-amber-400"
            >
              <option value="">{view === "upcoming" || view === "month" ? "Soonest first" : view === "past" ? "Latest first" : "Newest first"}</option>
              <option value="date:asc">Date (earliest)</option>
              <option value="date:desc">Date (latest)</option>
              <option value="balance:desc">Balance due (highest)</option>
              {canViewProfit && <option value="revenue:desc">Revenue (highest)</option>}
              {canViewProfit && <option value="profit:asc">Profit (lowest)</option>}
              <option value="name:asc">Name (A–Z)</option>
            </select>
          </div>
          {filtered.length === 0 ? emptyState : groups ? (
            groups.map((g) => (
              <div key={g.label}>
                <div className="px-4 pt-3 pb-1 flex justify-between text-[11px] font-bold uppercase tracking-wide text-stone-400">
                  <span>{g.label}</span><span>{g.rows.length}</span>
                </div>
                <div className="divide-y divide-stone-100">{g.rows.map(card)}</div>
              </div>
            ))
          ) : (
            <div className="divide-y divide-stone-100">{filtered.map(card)}</div>
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          {filtered.length === 0 ? emptyState : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50">
                  <SortTh col="name" label="Event" />
                  <SortTh col="date" label="Date" />
                  <SortTh col="guests" label="Guests" align="right" />
                  {canViewProfit && <SortTh col="revenue" label="Revenue" align="right" />}
                  <SortTh col="balance" label="Balance due" align="right" />
                  {canViewProfit && <SortTh col="profit" label="Profit" align="right" />}
                  <th className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-stone-500">Status</th>
                  <th className="px-3 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const e = r.e;
                  const isToday = r.offset === 0 || (r.ongoing && e.status !== "cancelled");
                  const muted = r.ended || e.status === "cancelled";
                  return (
                    <tr
                      key={e.id}
                      onClick={() => openEvent(e)}
                      className={`border-b border-stone-100 cursor-pointer transition-colors hover:bg-amber-50/40 ${isToday ? "shadow-[inset_3px_0_0_#d97706]" : ""}`}
                    >
                      <td className="px-3 py-3 min-w-[200px]">
                        <p className={`text-sm font-semibold ${muted ? "text-stone-500" : "text-stone-800"} ${e.status === "cancelled" ? "line-through" : ""}`}>{e.name}</p>
                        <p className="text-xs text-stone-500">{[e.client?.name, e.event_type, e.venue?.name].filter(Boolean).join(" · ") || "—"}</p>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <p className={`text-sm ${muted ? "text-stone-500" : "text-stone-700"}`}>{dateText(r)}{r.days > 1 && <span className="text-stone-400"> · {r.days} days</span>}</p>
                        <div className="mt-0.5">{relPill(r)}</div>
                      </td>
                      <td className="px-3 py-3 text-sm text-stone-600 text-right tabular-nums">{num(e.guest_count) || "—"}</td>
                      {canViewProfit && <td className="px-3 py-3 text-sm font-semibold text-stone-800 text-right tabular-nums whitespace-nowrap">{money(r.revenue)}</td>}
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <p className={`text-sm font-semibold tabular-nums ${r.balance > 0 ? "text-stone-800" : "text-stone-400"}`}>{r.balance > 0 ? money(r.balance) : "—"}</p>
                        <div className="mt-0.5">{payPill(r)}</div>
                      </td>
                      {canViewProfit && (
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <p className={`text-sm font-semibold tabular-nums ${profitClass(r.profit)}`}>{money(r.profit)}</p>
                          {r.revenue > 0 && <p className="text-[11px] text-stone-400 tabular-nums">{r.margin.toFixed(0)}%</p>}
                        </td>
                      )}
                      <td className="px-3 py-3" onClick={(ev) => ev.stopPropagation()}>{statusCell(e)}</td>
                      <td className="px-3 py-3" onClick={(ev) => ev.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          {takeButton(r)}
                          <ActionMenu items={menuItems(r)} label={`More actions for ${e.name}`} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {filtered.length > 0 && (
        <p className="mt-2 text-xs text-stone-400">
          {filtered.length} of {events.length} events{canViewProfit ? " · Revenue is after discount, before tax; Revenue − Cost = Profit" : ""}
        </p>
      )}

      {/* Delete dialog */}
      {deleting && (
        <Modal
          isOpen={!!deleting}
          onClose={() => !busy && setDeleting(null)}
          title={`Delete “${deleting.e.name}”?`}
          size="sm"
          footer={
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button type="button" onClick={() => setDeleting(null)} disabled={busy} className="h-11 px-4 rounded-xl border border-stone-300 text-sm font-semibold text-stone-600 hover:bg-stone-50">Keep event</button>
              {canCancel(deleting.e) && deleting.e.status !== "cancelled" && (
                <button type="button" onClick={cancelInstead} disabled={busy} className="h-11 px-4 rounded-xl border border-stone-300 text-sm font-semibold text-stone-700 hover:bg-stone-50">Cancel event instead</button>
              )}
              <button type="button" onClick={confirmDelete} disabled={busy} className="h-11 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50">Delete permanently</button>
            </div>
          }
        >
          <div className="space-y-3 text-sm text-stone-600">
            <p>This permanently removes the event and everything attached to it:</p>
            <ul className="list-disc pl-5 space-y-1">
              {(deleting.e.client_payments?.length || 0) > 0 && (
                <li>{deleting.e.client_payments!.length} client payment{deleting.e.client_payments!.length === 1 ? "" : "s"} ({money(deleting.paid)})</li>
              )}
              {(deleting.e.services?.length || 0) + (deleting.e.event_vendors?.length || 0) > 0 && (
                <li>{deleting.e.services?.length || 0} service{(deleting.e.services?.length || 0) === 1 ? "" : "s"} and {deleting.e.event_vendors?.length || 0} vendor assignment{(deleting.e.event_vendors?.length || 0) === 1 ? "" : "s"}</li>
              )}
              <li>Its invoice, expenses and activity history</li>
            </ul>
            {canCancel(deleting.e) && deleting.e.status !== "cancelled" && (
              <p className="rounded-lg bg-amber-50 text-amber-800 px-3 py-2">If the booking just isn't happening, cancel it instead. That keeps the payments and history.</p>
            )}
          </div>
        </Modal>
      )}

      {modalLoading && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 flex items-center gap-3 shadow-xl">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-amber-500 border-t-transparent" />
            <span className="text-stone-700 font-medium">Loading payment data…</span>
          </div>
        </div>
      )}

      {clientPaymentEvent && clientPaymentData && (
        <ClientPaymentModal
          isOpen={!!clientPaymentEvent}
          onClose={() => { setClientPaymentEvent(null); setClientPaymentData(null); }}
          eventId={clientPaymentEvent.id}
          customer={clientPaymentData.customer}
          payments={clientPaymentData.payments}
          onPaymentAdded={() => { loadEvents(true); if (clientPaymentEvent) openClientPaymentModal(clientPaymentEvent); }}
        />
      )}

      {vendorPaymentEvent && (
        <VendorPaymentModal
          isOpen={!!vendorPaymentEvent}
          onClose={() => setVendorPaymentEvent(null)}
          eventId={vendorPaymentEvent.id}
          onPaymentAdded={() => loadEvents(true)}
        />
      )}

      {printEvent && (
        <PrintableInvoice
          event={printEvent}
          businessInfo={{
            name: businessName,
            logoUrl: logoUrl || undefined,
            tagline: settings?.business?.tagline,
            address: settings?.business?.address || "123 Marine Drive, Mumbai – 400001",
            phone: settings?.business?.phone || "+91-98765-43210",
            email: settings?.business?.email || "info@shalimar.com",
            gstNumber: settings?.business?.gstNumber,
          }}
        />
      )}

      <AddEventModal isOpen={showAddEventModal} onClose={() => setShowAddEventModal(false)} onEventAdded={() => loadEvents(true)} />

      {editingEvent && (
        <EditEventModal
          isOpen={!!editingEvent}
          onClose={() => setEditingEvent(null)}
          onEventUpdated={() => loadEvents(true)}
          event={editingEvent}
        />
      )}
    </DashboardLayout>
  );
}
